import { useEffect, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  IndianRupee,
  ShoppingBag,
  TrendingUp,
  Users,
  Package,
  AlertTriangle,
  Truck,
  Activity,
  Timer,
} from "lucide-react";
import api from "../../lib/api";
import BriefingCard from "../../components/BriefingCard";

const RANGES = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "ytd", label: "YTD" },
];

export default function AdminDashboard() {
  const { me } = useOutletContext();
  const [range, setRange] = useState("30d");
  const [overview, setOverview] = useState(null);
  const [daily, setDaily] = useState([]);
  const [top, setTop] = useState([]);
  const [cities, setCities] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const [ov, dr, tp, ct, fn, ac] = await Promise.all([
        api.get(`/admin/stats/overview?range=${range}`),
        api.get(`/admin/stats/daily-revenue?range=${range}`),
        api.get(`/admin/stats/top-products?range=${range}&limit=5`),
        api.get(`/admin/stats/by-city?range=${range}`),
        api.get(`/admin/stats/order-funnel?range=${range}`),
        api.get(`/admin/stats/recent-activity?limit=10`),
      ]);
      if (cancelled) return;
      setOverview(ov.data);
      setDaily(dr.data.series || []);
      setTop(tp.data.items || []);
      setCities(ct.data.items || []);
      setFunnel(fn.data);
      setActivity(ac.data.items || []);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [range]);

  const maxRev = Math.max(1, ...daily.map((d) => d.revenue));
  const maxCity = Math.max(1, ...cities.map((c) => c.revenue));
  const maxFunnel = funnel ? Math.max(1, ...funnel.stages.map((s) => s.count)) : 1;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Daily AI briefing */}
      <BriefingCard />

      {/* Heading + range */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="label-overline text-jow-gold">Admin · Overview</span>
          <h1 className="font-display text-4xl text-jow-navy mt-1">
            Welcome, {me?.name?.split(" ")[0] || "friend"}.
          </h1>
          <p className="text-sm text-jow-navy/55 mt-1">
            Here's what's humming at JewelleryOnWheels.
          </p>
        </div>
        <div className="inline-flex border border-jow-navy/15 bg-white" data-testid="range-selector">
          {RANGES.map((r) => (
            <button
              key={r.value}
              onClick={() => setRange(r.value)}
              className={`px-4 py-2 text-[11px] uppercase tracking-widest transition ${
                range === r.value
                  ? "bg-jow-navy text-jow-cream"
                  : "text-jow-navy/60 hover:text-jow-navy"
              }`}
              data-testid={`range-${r.value}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <Kpi Icon={IndianRupee} label="Revenue" value={`₹${(overview?.revenue || 0).toLocaleString("en-IN")}`} testid="kpi-revenue" accent />
        <Kpi Icon={ShoppingBag} label="Orders" value={overview?.orders || 0} testid="kpi-orders" />
        <Kpi Icon={TrendingUp} label="AOV" value={`₹${(overview?.aov || 0).toLocaleString("en-IN")}`} testid="kpi-aov" />
        <Kpi Icon={Users} label="New customers" value={overview?.new_customers || 0} testid="kpi-new-customers" />
        <Kpi Icon={AlertTriangle} label="Low stock" value={overview?.low_stock_count || 0} testid="kpi-low-stock" warn={(overview?.low_stock_count || 0) > 0} />
        <Kpi Icon={Package} label="Paid %" value={`${overview?.paid_pct || 0}%`} testid="kpi-paid-pct" />
        <Kpi Icon={Timer} label="Reservations placed" value={overview?.reservations?.placed || 0} testid="kpi-res-placed" />
        <Kpi Icon={Activity} label="Newsletter subs" value={overview?.newsletter_subs || 0} testid="kpi-newsletter" />
        <Kpi Icon={Truck} label="Active rikshaws" value={overview?.active_rikshaws || 0} testid="kpi-rikshaws" />
      </div>

      {/* Daily revenue */}
      <section className="bg-white border border-jow-navy/10 p-5" data-testid="chart-daily-revenue">
        <div className="flex items-center justify-between mb-3">
          <span className="label-overline text-jow-navy">Daily revenue</span>
          <span className="text-[10px] text-jow-navy/50">
            {daily.length} days · peak ₹{maxRev.toLocaleString("en-IN")}
          </span>
        </div>
        <div className="flex items-end h-36 gap-1">
          {daily.map((d) => {
            const h = Math.max(2, Math.round((d.revenue / maxRev) * 100));
            return (
              <div
                key={d.date}
                className="flex-1 bg-jow-gold/25 hover:bg-jow-gold transition-colors relative group cursor-pointer"
                style={{ height: `${h}%` }}
                data-testid={`bar-${d.date}`}
                title={`${d.date}: ₹${d.revenue.toLocaleString("en-IN")}`}
              >
                <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] text-jow-navy opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none">
                  ₹{d.revenue}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Top products */}
        <section className="bg-white border border-jow-navy/10 p-5" data-testid="chart-top-products">
          <p className="label-overline text-jow-navy mb-4">Top 5 products</p>
          {top.length === 0 && (
            <p className="text-xs text-jow-navy/50">No paid orders in this period.</p>
          )}
          <ol className="space-y-3">
            {top.map((p, i) => (
              <li key={p.product_id} className="flex items-center gap-3 text-sm" data-testid={`top-prod-${i}`}>
                <span className="font-display text-2xl text-jow-gold w-6">{i + 1}</span>
                {p.image && <img src={p.image} alt="" className="w-10 h-10 object-cover" />}
                <div className="flex-1 min-w-0">
                  <p className="text-jow-navy truncate">{p.name}</p>
                  <p className="text-[10px] text-jow-navy/50 uppercase tracking-widest">
                    {p.units} units
                  </p>
                </div>
                <span className="font-display text-jow-navy">
                  ₹{p.revenue.toLocaleString("en-IN")}
                </span>
              </li>
            ))}
          </ol>
        </section>

        {/* Revenue by city */}
        <section className="bg-white border border-jow-navy/10 p-5" data-testid="chart-by-city">
          <p className="label-overline text-jow-navy mb-4">Revenue by city</p>
          {cities.length === 0 && (
            <p className="text-xs text-jow-navy/50">No city data.</p>
          )}
          <ul className="space-y-3">
            {cities.slice(0, 8).map((c) => (
              <li key={c.city} data-testid={`city-${c.city}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-jow-navy">{c.city}</span>
                  <span className="text-jow-navy/60">₹{c.revenue.toLocaleString("en-IN")}</span>
                </div>
                <div className="h-1.5 bg-jow-navy/5 mt-1">
                  <div
                    className="h-full bg-jow-pink"
                    style={{ width: `${(c.revenue / maxCity) * 100}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>

      {/* Funnel + Activity */}
      <div className="grid lg:grid-cols-2 gap-4">
        <section className="bg-white border border-jow-navy/10 p-5" data-testid="chart-funnel">
          <p className="label-overline text-jow-navy mb-4">Order funnel</p>
          <ul className="space-y-2">
            {funnel?.stages.map((s) => (
              <li key={s.status} className="flex items-center gap-3 text-sm">
                <span className="w-28 text-jow-navy/70 capitalize">{s.status.replaceAll("_", " ")}</span>
                <div className="flex-1 h-5 bg-jow-navy/5 relative">
                  <div
                    className="h-full bg-jow-navy/80"
                    style={{ width: `${(s.count / maxFunnel) * 100}%` }}
                    data-testid={`funnel-bar-${s.status}`}
                  />
                </div>
                <span className="font-display text-jow-navy">{s.count}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-[11px] text-jow-navy/55 flex items-center gap-4">
            <span><strong className="text-jow-pink">{funnel?.cancelled ?? 0}</strong> cancelled</span>
            <span><strong className="text-jow-gold">{funnel?.reserved ?? 0}</strong> reserved</span>
          </p>
        </section>

        <section className="bg-white border border-jow-navy/10 p-5" data-testid="feed-activity">
          <p className="label-overline text-jow-navy mb-4">Recent activity</p>
          <ul className="space-y-3">
            {activity.length === 0 && (
              <p className="text-xs text-jow-navy/50">Nothing recent.</p>
            )}
            {activity.map((e, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: 6 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.03 }}
                className="flex items-start gap-2 text-sm"
              >
                <span className="w-1.5 h-1.5 mt-2 bg-jow-gold rounded-full" />
                <div className="flex-1">
                  <p className="text-jow-navy">{e.title}</p>
                  <p className="text-[10px] text-jow-navy/50">
                    {e.detail} · {new Date(e.at).toLocaleString()}
                  </p>
                </div>
              </motion.li>
            ))}
          </ul>
        </section>
      </div>

      <div className="grid md:grid-cols-4 gap-3 text-center">
        <Link to="/admin/orders" className="bg-white border border-jow-navy/10 p-4 hover:border-jow-gold transition" data-testid="quick-orders">
          <Receipt label="Manage orders" />
        </Link>
        <Link to="/admin/inventory" className="bg-white border border-jow-navy/10 p-4 hover:border-jow-gold transition" data-testid="quick-inventory">
          <Receipt label="Inventory & ledger" />
        </Link>
        <Link to="/admin/fleet" className="bg-white border border-jow-navy/10 p-4 hover:border-jow-gold transition" data-testid="quick-fleet">
          <Receipt label="Fleet overview" />
        </Link>
        <Link to="/admin/reports" className="bg-white border border-jow-navy/10 p-4 hover:border-jow-gold transition" data-testid="quick-reports">
          <Receipt label="Reports & CSV" />
        </Link>
      </div>
    </div>
  );
}

function Kpi({ Icon, label, value, testid, accent, warn }) {
  return (
    <div
      className={`bg-white border p-4 ${
        accent ? "border-jow-gold/60" : warn ? "border-jow-pink/60" : "border-jow-navy/10"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon size={13} className={accent ? "text-jow-gold" : warn ? "text-jow-pink" : "text-jow-navy/50"} />
        <span className="label-overline text-jow-navy/55 text-[9px]">{label}</span>
      </div>
      <p
        className="font-display text-2xl text-jow-navy"
        data-testid={`${testid}-value`}
      >
        {value}
      </p>
    </div>
  );
}

function Receipt({ label }) {
  return (
    <div className="flex items-center justify-center gap-2">
      <span className="text-[11px] uppercase tracking-widest text-jow-navy/70 hover:text-jow-navy">
        {label} →
      </span>
    </div>
  );
}
